import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Plus, Upload, Copy, Check, ArrowRight, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import toast from 'react-hot-toast';
import Header from '../components/Header';
import CryptoTicker from '../components/CryptoTicker';
import Footer from '../components/Footer';
import CurrencyIcon from '../components/CurrencyIcon';
import { FadeUp } from '../components/Motion';
import api, { cryptoApi } from '../utils/api';
import FiatRates from '../components/FiatRates';
import { RUSSIAN_BANKS, bankLogo, detectCardBrand } from '../utils/bankUtils';

const STATUS_STYLE = {
  open: 'bg-amber-100 text-amber-700', matched: 'bg-blue-100 text-blue-700',
  escrow_pending: 'bg-violet-100 text-violet-700', escrow_received: 'bg-indigo-100 text-indigo-700',
  fiat_sent: 'bg-sky-100 text-sky-700', completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-gray-100 text-gray-500', rejected: 'bg-rose-100 text-rose-700',
};

const TradingDirect = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const p = { ...(t.p2p || {}), ...(t.p2pExtra || {}) };
  const [tab, setTab] = useState('book');
  const [orders, setOrders] = useState([]);
  const [myOrders, setMyOrders] = useState([]);
  const [wallets, setWallets] = useState({});
  const [rates, setRates] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [copied, setCopied] = useState('');
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [form, setForm] = useState({ side: 'sell', crypto: 'BTC', network: 'ERC20', amount_crypto: '', price_per_unit: '', fiat_currency: 'EUR', bank_name: '', bank_card: '', wallet_address: '' });

  const fetchAll = useCallback(async () => {
    try { const r = await cryptoApi.getRates(); setRates(r.data); } catch {}
    if (!user) return;
    try { const r = await api.get('/p2p/orders'); setOrders(r.data); } catch {}
    try { const r = await api.get('/p2p/my-orders'); setMyOrders(r.data); } catch {}
    try { const r = await api.get('/p2p/wallets'); setWallets(r.data); } catch {}
    setLoadingOrders(false);
  }, [user]);

  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 10000); return () => clearInterval(i); }, [fetchAll]);

  const [takeTarget, setTakeTarget] = useState(null);
  const [takeInput, setTakeInput] = useState('');
  const [platform, setPlatform] = useState({ p2p_enabled: true, closed_message: '' });
  const [fiatRates, setFiatRates] = useState(null);

  useEffect(() => {
    api.get('/settings/public').then((r) => setPlatform(r.data)).catch(() => {});
    api.get('/fiat/rates').then((r) => setFiatRates(r.data)).catch(() => {});
  }, []);

  const marketPrice = (() => {
    const c = rates[form.crypto] || {};
    if (form.fiat_currency === 'EUR') return c.eur || 0;
    if (form.fiat_currency === 'USD') return c.usd || 0;
    return (c.usd || 0) * (fiatRates?.rates?.RUB || 0);
  })();

  const copyAddr = (a) => { navigator.clipboard.writeText(a); setCopied(a); toast.success('Copié !'); setTimeout(() => setCopied(''), 2000); };

  const submitOrder = async () => {
    try {
      await api.post('/p2p/orders', { ...form, amount_crypto: parseFloat(form.amount_crypto), price_per_unit: null });
      toast.success(p.waiting || 'Ordre créé, en attente d\'une contrepartie');
      setShowCreate(false); setTab('mine'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  useEffect(() => {
    if (showCreate && !platform.p2p_enabled) setShowCreate(false);
  }, [showCreate, platform.p2p_enabled]);

  const takeOrder = (order) => { setTakeInput(''); setTakeTarget(order); };

  const confirmTake = async () => {
    if (!takeInput.trim()) { toast.error('Champ requis'); return; }
    const takerSide = takeTarget.side === 'sell' ? 'buy' : 'sell';
    const body = takerSide === 'buy' ? { wallet_address: takeInput.trim() } : { bank_card: takeInput.trim() };
    try {
      await api.post(`/p2p/orders/${takeTarget.id}/match`, body);
      toast.success(p.matched || 'Contrepartie trouvée !');
      setTakeTarget(null); setTab('mine'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const declareSent = async (id) => {
    try { await api.post(`/p2p/orders/${id}/escrow-sent`); toast.success('Déclaré !'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const uploadReceipt = async (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      await api.post(`/p2p/orders/${id}/receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(p.fiatSent || 'Reçu envoyé !'); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const cancelOrder = async (id) => {
    try { await api.post(`/p2p/orders/${id}/cancel`); toast.success(p.cancelled || 'Annulé'); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const walletFor = (o) => {
    if (o.crypto === 'BTC') return wallets.BTC;
    return o.network === 'TRC20' ? wallets.USDT_TRC20 : wallets.USDT_ERC20;
  };

  const isSeller = (o) => o.i_am_seller ?? ((o.my_role === 'maker' && o.side === 'sell') || (o.my_role === 'taker' && o.side === 'buy'));

  return (
    <div className="min-h-screen bg-[#F5F6F7]">
      <CryptoTicker />
      <Header />

      {/* Hero */}
      <div className="bg-black px-6 pt-16 pb-24">
        <div className="max-w-6xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center space-x-2 gradient-pink-violet text-white px-4 py-1.5 rounded-full text-sm font-bold mb-6">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span><span>{p.badge || 'Nouveau'}</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="text-white font-extrabold text-4xl md:text-6xl tracking-[-0.03em] mb-4" style={{ fontFamily: 'Manrope' }} data-testid="p2p-title">
            {p.title || 'Trading Direct'}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            className="text-white/60 text-lg max-w-2xl mx-auto mb-8">{p.subtitle}</motion.p>
          <div className="flex flex-wrap items-center justify-center gap-6 text-white/50 text-sm">
            <div className="flex items-center space-x-2"><Shield className="w-4 h-4" /><span>{p.escrowInfo || 'Fonds bloqués max 45 min'}</span></div>
            <div className="flex items-center space-x-2"><CurrencyIcon symbol="BTC" size={16} /><CurrencyIcon symbol="USDT" size={16} /><span>BTC · USDT</span></div>
            <div className="flex items-center space-x-2"><span>{p.commission || 'Commission vendeur 7%'}</span></div>
          </div>
        </div>
      </div>

      <div className="px-6 pb-16 -mt-12">
        <div className="max-w-6xl mx-auto">
          {!user ? (
            <FadeUp className="bg-white rounded-3xl p-12 text-center shadow-[0_12px_40px_rgb(0,0,0,0.08)]" data-testid="p2p-login-required">
              <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <h3 className="text-2xl font-extrabold text-gray-900 mb-3 tracking-tight" style={{ fontFamily: 'Manrope' }}>{p.presentTitle || 'Tradez en direct, entre vous'}</h3>
              <p className="text-gray-500 mb-8 max-w-md mx-auto">{p.presentDesc}</p>
              <Link to="/auth"><button className="btn-pill-dark px-8 py-4" data-testid="p2p-login-btn">{t.nav?.login || 'Se connecter'}</button></Link>
            </FadeUp>
          ) : (
            <>
              {!platform.p2p_enabled && (
                <FadeUp className="bg-gradient-to-r from-rose-500 to-orange-500 rounded-3xl p-6 mb-6 text-white flex items-start space-x-4 shadow-[0_12px_40px_rgb(244,63,94,0.25)]" data-testid="p2p-closed-banner">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                    <Clock className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-extrabold text-lg" style={{ fontFamily: 'Manrope' }}>{p.channelClosed || 'Trading Direct temporairement fermé'}</div>
                    <p className="text-white/90 text-sm mt-1">{platform.closed_message || 'Les échanges sont temporairement suspendus. Merci de réessayer plus tard.'}</p>
                  </div>
                </FadeUp>
              )}
              <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
                <div className="bg-white rounded-full p-1.5 inline-flex shadow-[0_4px_20px_rgb(0,0,0,0.05)]">
                  <button onClick={() => setTab('book')} className={`px-6 py-2.5 rounded-full font-bold text-sm transition-colors ${tab === 'book' ? 'bg-[#191C1F] text-white' : 'text-gray-500'}`} data-testid="tab-orderbook">{p.orderbook || "Carnet d'ordres"}</button>
                  <button onClick={() => setTab('mine')} className={`px-6 py-2.5 rounded-full font-bold text-sm transition-colors ${tab === 'mine' ? 'bg-[#191C1F] text-white' : 'text-gray-500'}`} data-testid="tab-my-orders">{p.myOrders || 'Mes ordres'} ({myOrders.length})</button>
                </div>
                <button onClick={() => setShowCreate(true)} disabled={!platform.p2p_enabled}
                  className="btn-pill-dark px-6 py-3 flex items-center space-x-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed" data-testid="create-order-btn">
                  <Plus className="w-4 h-4" /><span>{p.createOrder || 'Créer un ordre'}</span>
                </button>
              </div>

              <div className="mb-6"><FiatRates /></div>

              {tab === 'book' && (
                <FadeUp className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid="orderbook-section">
                  {loadingOrders ? (
                    <div className="space-y-3" data-testid="orderbook-loading">
                      {[0, 1, 2].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-2xl animate-pulse" />)}
                    </div>
                  ) : orders.length === 0 ? (
                    <div className="text-center py-14 text-gray-400">{p.noOrders || 'Aucun ordre disponible. Créez le vôtre !'}</div>
                  ) : (
                    <div className="space-y-3">
                      {orders.map((o) => (
                        <div key={o.id} className="flex items-center justify-between flex-wrap gap-4 p-5 bg-[#F5F6F7] rounded-2xl hover:bg-gray-100 transition-colors" data-testid={`order-${o.id}`}>
                          <div className="flex items-center space-x-4">
                            <CurrencyIcon symbol={o.crypto} size={36} />
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${o.side === 'sell' ? 'bg-rose-100 text-rose-700' : 'bg-emerald-100 text-emerald-700'}`}>
                                  {o.side === 'sell' ? (p.sell || 'Vendre') : (p.buy || 'Acheter')}
                                </span>
                                <span className="font-bold text-gray-900 font-mono-data">{o.amount_crypto} {o.crypto}</span>
                                {o.crypto === 'USDT' && <span className="text-xs text-gray-400">{o.network}</span>}
                              </div>
                              <div className="text-sm text-gray-400 mt-0.5">{o.maker_masked} · {new Date(o.created_at).toLocaleString('fr-FR')}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-gray-900 font-mono-data">{o.price_per_unit.toLocaleString('fr-FR')} {o.fiat_currency}/u</div>
                            <div className="text-sm text-gray-400 font-mono-data">{p.total || 'Total'}: {o.amount_fiat.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {o.fiat_currency}</div>
                            {o.side === 'sell' && o.buyer_receives_crypto != null && (
                              <div className="text-xs font-semibold text-emerald-600 font-mono-data">{p.buyerGets || "L'acheteur reçoit"}: {o.buyer_receives_crypto.toFixed(6)} {o.crypto} (−7%)</div>
                            )}
                          </div>
                          <button onClick={() => takeOrder(o)} disabled={!platform.p2p_enabled}
                            className="btn-pill-dark px-5 py-2.5 text-sm flex items-center space-x-2 disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`take-order-${o.id}`}>
                            <span>{p.takeOrder || 'Prendre cet ordre'}</span><ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </FadeUp>
              )}

              {tab === 'mine' && (
                <div className="space-y-4" data-testid="my-orders-section">
                  {loadingOrders && <div className="bg-white rounded-3xl p-14 shadow-[0_8px_30px_rgb(0,0,0,0.05)]"><div className="h-16 bg-gray-100 rounded-2xl animate-pulse" /></div>}
                  {!loadingOrders && myOrders.length === 0 && <div className="bg-white rounded-3xl p-14 text-center text-gray-400 shadow-[0_8px_30px_rgb(0,0,0,0.05)]">{p.noOrders}</div>}
                  {myOrders.map((o) => {
                    const w = walletFor(o);
                    const seller = isSeller(o);
                    return (
                      <div key={o.id} className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.05)]" data-testid={`my-order-${o.id}`}>
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                          <div className="flex items-center space-x-3">
                            <CurrencyIcon symbol={o.crypto} size={32} />
                            <div>
                              <div className="font-bold text-gray-900 font-mono-data">{o.amount_crypto} {o.crypto} → {o.amount_fiat.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {o.fiat_currency}</div>
                              <div className="text-xs text-gray-400">
                                {o.order_number && <span className="font-mono-data font-bold text-gray-600" data-testid={`order-number-${o.id}`}>N° {o.order_number}</span>}
                                {o.order_number && ' · '}
                                {seller ? (p.sell || 'Vendeur') : (p.buy || 'Acheteur')} · {o.crypto === 'USDT' ? o.network : 'Bitcoin'}
                              </div>
                            </div>
                          </div>
                          <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${STATUS_STYLE[o.status] || 'bg-gray-100 text-gray-500'}`} data-testid={`order-status-${o.id}`}>
                            {p[{open:'waiting',matched:'matched',escrow_pending:'escrowPending',escrow_received:'escrowReceived',fiat_sent:'fiatSent',completed:'completed',cancelled:'cancelled',rejected:'cancelled'}[o.status]] || o.status}
                          </span>
                        </div>

                        {o.has_counterparty && (
                          <div className="mb-3" data-testid={`counterparty-badge-${o.id}`}>
                            {o.counterparty_type === 'company' ? (
                              <span className="inline-flex items-center space-x-1.5 text-xs font-bold gradient-pink-violet text-white px-3 py-1.5 rounded-full">
                                <Shield className="w-3.5 h-3.5" /><span>{p.companyCounterparty || 'Contrepartie : alphaswapp — vous êtes Taker'}</span>
                              </span>
                            ) : (
                              <span className="inline-flex items-center space-x-1.5 text-xs font-bold bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full">
                                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span><span>{p.p2pCounterparty || 'P2P — échange entre clients'} · {o.my_role === 'maker' ? 'Maker' : 'Taker'}</span>
                              </span>
                            )}
                          </div>
                        )}

                        {!['completed', 'cancelled', 'rejected', 'open'].includes(o.status) && (
                          <div className="flex items-start space-x-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3" data-testid={`delay-warning-${o.id}`}>
                            <Clock className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>{p.delayWarning || 'Le traitement peut nécessiter jusqu\'à 24 heures supplémentaires en cas de vérification renforcée, de contrôle d\'intégrité du paiement ou d\'imprévu lié aux parties. Merci de votre patience.'}</span>
                          </div>
                        )}

                        {o.admin_public_note && (
                          <div className="flex items-start space-x-2 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3" data-testid={`admin-note-${o.id}`}>
                            <Shield className="w-4 h-4 shrink-0 mt-0.5" />
                            <span><strong>{p.operatorInfo || "Information de l'opérateur"} :</strong> {o.admin_public_note}</span>
                          </div>
                        )}

                        {o.status === 'matched' && seller && (
                          <div className="bg-[#F5F6F7] rounded-2xl p-4 mb-3">
                            <div className="text-sm font-semibold text-gray-700 mb-2">
                              {o.admin_payment_info
                                ? (p.sendCryptoAdmin || "Envoyez la crypto à l'adresse communiquée par l'opérateur :")
                                : (p.sendCrypto || 'Envoyez la crypto au wallet alphaswapp')}{!o.admin_payment_info && w ? ` (${w.network}) :` : ''}
                            </div>
                            {(o.admin_payment_info || w) && (
                              <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-gray-200">
                                <span className="font-mono-data text-sm text-gray-800 truncate pr-2" data-testid={`seller-payment-info-${o.id}`}>{o.admin_payment_info || w.address}</span>
                                <button onClick={() => copyAddr(o.admin_payment_info || w.address)} className="p-1.5 hover:bg-gray-100 rounded-full">
                                  {copied === (o.admin_payment_info || w.address) ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                                </button>
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-3">
                              <span className="text-xs text-gray-400 flex items-center"><Clock className="w-3.5 h-3.5 mr-1" />{p.escrowInfo}</span>
                              <button onClick={() => declareSent(o.id)} className="btn-pill-dark px-5 py-2 text-sm" data-testid={`escrow-sent-${o.id}`}>{p.iSent || "J'ai envoyé la crypto"}</button>
                            </div>
                          </div>
                        )}

                        {!seller && ['matched', 'escrow_pending', 'escrow_received'].includes(o.status) && (
                          <div className="bg-[#F5F6F7] rounded-2xl p-4 mb-3">
                            {o.status === 'escrow_received' ? (
                              <>
                                <div className="text-sm font-semibold text-gray-700 mb-2">
                                  {o.admin_payment_info
                                    ? (p.payToCardAdmin || "Envoyez le paiement à la carte communiquée par l'opérateur, puis envoyez le reçu :")
                                    : (p.waitingCardInfo || "L'opérateur va vous communiquer les coordonnées de paiement ici. Vous pouvez aussi le contacter via le chat support.")}
                                </div>
                                {o.admin_payment_info && (
                                  <div className="flex items-center justify-between bg-white rounded-xl p-3 border border-gray-200 mb-3">
                                    <span className="font-mono-data text-sm text-gray-800 truncate pr-2" data-testid={`buyer-payment-info-${o.id}`}>{o.admin_payment_info}</span>
                                    <button onClick={() => copyAddr(o.admin_payment_info)} className="p-1.5 hover:bg-gray-100 rounded-full">
                                      {copied === o.admin_payment_info ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4 text-gray-400" />}
                                    </button>
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-sm font-semibold text-gray-700 mb-2">{p.waitingEscrow || 'En attente du séquestre crypto du vendeur...'}</div>
                            )}
                            {(o.status === 'escrow_received' || o.status === 'escrow_pending') && (
                              <label className="btn-pill-dark px-5 py-2.5 text-sm inline-flex items-center space-x-2 cursor-pointer">
                                <Upload className="w-4 h-4" /><span>{p.uploadReceipt || 'Envoyer le reçu bancaire'}</span>
                                <input type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => e.target.files[0] && uploadReceipt(o.id, e.target.files[0])} data-testid={`receipt-input-${o.id}`} />
                              </label>
                            )}
                          </div>
                        )}

                        {seller && ['escrow_pending', 'escrow_received', 'fiat_sent'].includes(o.status) && (
                          <div className="bg-[#F5F6F7] rounded-2xl p-4 mb-3 text-sm font-semibold text-gray-700" data-testid={`seller-status-info-${o.id}`}>
                            {o.status === 'escrow_pending' && (p.sellerEscrowPending || "Crypto déclarée envoyée — l'opérateur vérifie la réception du séquestre.")}
                            {o.status === 'escrow_received' && (p.sellerEscrowReceived || "Séquestre confirmé ✓ — en attente du paiement de l'acheteur.")}
                            {o.status === 'fiat_sent' && (p.sellerFiatSent || "L'acheteur a déclaré le paiement — l'opérateur vérifie le reçu avant de finaliser.")}
                          </div>
                        )}

                        {o.status === 'open' && o.my_role === 'maker' && (
                          <button onClick={() => cancelOrder(o.id)} className="text-sm font-semibold px-4 py-1.5 rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors" data-testid={`cancel-order-${o.id}`}>{p.cancelBtn || 'Annuler'}</button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* How it works */}
          <FadeUp className="mt-14 bg-black rounded-3xl p-8 md:p-10 text-white">
            <h3 className="text-2xl font-extrabold mb-8 tracking-tight" style={{ fontFamily: 'Manrope' }}>{p.howTitle || 'Comment ça marche ?'}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[p.how1, p.how2, p.how3].map((txt, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6">
                  <div className="w-9 h-9 rounded-full bg-white text-gray-900 font-extrabold flex items-center justify-center mb-4">{i + 1}</div>
                  <p className="text-white/60 text-sm leading-relaxed">{txt}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </div>

      {/* Create order modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setShowCreate(false)} data-testid="create-order-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-extrabold mb-6 text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>{p.createOrder || 'Créer un ordre'}</h3>
            <div className="flex mb-4 bg-[#F5F6F7] rounded-full p-1">
              {['buy', 'sell'].map(s => (
                <button key={s} onClick={() => setForm({ ...form, side: s })}
                  className={`flex-1 py-2.5 rounded-full font-bold text-sm transition-colors ${form.side === s ? 'bg-[#191C1F] text-white' : 'text-gray-500'}`}
                  data-testid={`side-${s}`}>{s === 'buy' ? (p.buy || 'Acheter') : (p.sell || 'Vendre')}</button>
              ))}
            </div>
            <div className="flex space-x-2 mb-4">
              {['BTC', 'USDT'].map(c => (
                <button key={c} onClick={() => setForm({ ...form, crypto: c })}
                  className={`flex-1 py-2.5 rounded-2xl font-bold text-sm border transition-colors flex items-center justify-center space-x-2 ${form.crypto === c ? 'border-gray-900 bg-gray-50' : 'border-gray-200 text-gray-500'}`}
                  data-testid={`crypto-${c}`}><CurrencyIcon symbol={c} size={18} /><span>{c}</span></button>
              ))}
            </div>
            {form.crypto === 'USDT' && (
              <div className="flex space-x-2 mb-4">
                {['ERC20', 'TRC20'].map(n => (
                  <button key={n} onClick={() => setForm({ ...form, network: n })}
                    className={`flex-1 py-2 rounded-2xl font-semibold text-xs border ${form.network === n ? 'border-gray-900 bg-gray-50 text-gray-900' : 'border-gray-200 text-gray-400'}`}
                    data-testid={`network-${n}`}>{n}</button>
                ))}
              </div>
            )}
            <input type="number" placeholder={`${p.amount || 'Montant'} (${form.crypto})`} value={form.amount_crypto}
              onChange={(e) => setForm({ ...form, amount_crypto: e.target.value })}
              className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-3.5 mb-3 outline-none font-mono-data" data-testid="order-amount-input" />
            <div className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-3.5 mb-3 flex items-center justify-between" data-testid="market-price-display">
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-400">{p.autoPrice || 'Prix unitaire — cours du marché (automatique)'}</div>
                <div className="font-mono-data font-extrabold text-gray-900 text-lg">
                  {marketPrice ? marketPrice.toLocaleString('fr-FR', { maximumFractionDigits: 2 }) : '...'} {form.fiat_currency === 'EUR' ? '€' : form.fiat_currency === 'USD' ? '$' : '₽'}
                  <span className="text-xs text-gray-400 font-normal"> / {form.crypto}</span>
                </div>
              </div>
              <span className="flex items-center space-x-1.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                <span>{p.liveRate || 'Temps réel'}</span>
              </span>
            </div>
            <div className="flex space-x-2 mb-4">
              {['EUR', 'USD', 'RUB'].map((c) => (
                <button key={c} onClick={() => setForm({ ...form, fiat_currency: c })}
                  className={`flex-1 py-2 rounded-2xl font-bold text-xs border transition-colors ${form.fiat_currency === c ? 'border-gray-900 bg-gray-50 text-gray-900' : 'border-gray-200 text-gray-400'}`}
                  data-testid={`fiat-${c}`}>{c === 'EUR' ? '€ EUR' : c === 'USD' ? '$ USD' : '₽ RUB'}</button>
              ))}
            </div>
            {form.side === 'sell' ? (
              <>
                <div className="mb-3">
                  <div className="text-[10px] uppercase font-bold text-gray-400 mb-2">{p.chooseBank || 'Votre banque'}</div>
                  <div className="flex space-x-2 overflow-x-auto pb-2" data-testid="bank-selector">
                    {RUSSIAN_BANKS.map((b) => (
                      <button key={b.name} onClick={() => setForm({ ...form, bank_name: b.name })}
                        className={`flex flex-col items-center px-3 py-2 rounded-2xl border shrink-0 transition-colors ${form.bank_name === b.name ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-400'}`}
                        data-testid={`bank-${b.name}`}>
                        <span className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: b.color + '22' }}>
                          <img src={bankLogo(b.domain)} alt={b.name} className="w-6 h-6 rounded-full object-contain"
                            onError={(e) => { e.target.outerHTML = `<span style="color:${b.color};font-weight:800;font-size:13px">${b.name[0]}</span>`; }} />
                        </span>
                        <span className="text-[10px] font-bold text-gray-600 mt-1 whitespace-nowrap">{b.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <input type="text" placeholder={p.bankName || 'Banque (ou autre banque)'} value={form.bank_name}
                  onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                  className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-3.5 mb-3 outline-none" data-testid="order-bank-name" />
                <div className="relative mb-3">
                  <input type="text" placeholder={p.bankCard || 'Carte bancaire'} value={form.bank_card}
                    onChange={(e) => setForm({ ...form, bank_card: e.target.value })}
                    className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-3.5 pr-24 outline-none font-mono-data" data-testid="order-bank-card" />
                  {detectCardBrand(form.bank_card) && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-extrabold text-white px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: detectCardBrand(form.bank_card).color }} data-testid="card-brand-badge">
                      {detectCardBrand(form.bank_card).brand}
                    </span>
                  )}
                </div>
                {parseFloat(form.amount_crypto) > 0 ? (
                  <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3.5 mb-4 text-sm" data-testid="seller-net-preview">
                    <div className="flex justify-between text-gray-500 text-xs mb-1"><span>{p.grossAmount || 'Montant brut'}</span><span className="font-mono-data">{parseFloat(form.amount_crypto).toFixed(6)} {form.crypto}</span></div>
                    <div className="flex justify-between text-rose-600 text-xs mb-1"><span>{p.commission7 || 'Commission 7%'}</span><span className="font-mono-data">−{(parseFloat(form.amount_crypto) * 0.07).toFixed(6)} {form.crypto}</span></div>
                    <div className="flex justify-between font-bold text-gray-900"><span>{p.buyerWillGet || "Transmis à l'acheteur"}</span><span className="font-mono-data">{(parseFloat(form.amount_crypto) * 0.93).toFixed(6)} {form.crypto}</span></div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mb-4">{p.commission || 'Commission vendeur 7%'}</p>
                )}
              </>
            ) : (
              <>
                <input type="text" placeholder={p.walletAddr || 'Adresse de votre wallet'} value={form.wallet_address}
                  onChange={(e) => setForm({ ...form, wallet_address: e.target.value })}
                  className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-3.5 mb-3 outline-none font-mono-data" data-testid="order-wallet-address" />
                {parseFloat(form.amount_crypto) > 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 mb-4 text-sm" data-testid="buyer-net-preview">
                    <div className="flex justify-between text-gray-500 text-xs mb-1"><span>{p.grossAmount || 'Montant brut'}</span><span className="font-mono-data">{parseFloat(form.amount_crypto).toFixed(6)} {form.crypto}</span></div>
                    <div className="flex justify-between text-rose-600 text-xs mb-1"><span>{p.commission7 || 'Commission 7%'}</span><span className="font-mono-data">−{(parseFloat(form.amount_crypto) * 0.07).toFixed(6)} {form.crypto}</span></div>
                    <div className="flex justify-between font-bold text-emerald-700"><span>{p.youReceive || 'Vous recevrez'}</span><span className="font-mono-data">{(parseFloat(form.amount_crypto) * 0.93).toFixed(6)} {form.crypto}</span></div>
                  </div>
                )}
              </>
            )}
            <div className="flex space-x-3">
              <button onClick={submitOrder} className="flex-1 btn-pill-dark py-3.5" data-testid="submit-order-btn">{p.createOrder || 'Créer'}</button>
              <button onClick={() => setShowCreate(false)} className="flex-1 btn-pill-ghost py-3.5">{p.cancelBtn || 'Annuler'}</button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Take order modal */}
      {takeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setTakeTarget(null)} data-testid="take-order-modal">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-extrabold mb-2 text-gray-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>{p.takeOrder || 'Prendre cet ordre'}</h3>
            <p className="text-sm text-gray-500 mb-5 font-mono-data">{takeTarget.amount_crypto} {takeTarget.crypto} · {takeTarget.amount_fiat?.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} {takeTarget.fiat_currency}</p>
            {takeTarget.side === 'sell' && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-3.5 mb-4 text-sm" data-testid="take-net-preview">
                <div className="flex justify-between text-rose-600 text-xs mb-1"><span>{p.commission7 || 'Commission 7%'}</span><span className="font-mono-data">−{(takeTarget.amount_crypto * 0.07).toFixed(6)} {takeTarget.crypto}</span></div>
                <div className="flex justify-between font-bold text-emerald-700"><span>{p.youReceive || 'Vous recevrez'}</span><span className="font-mono-data">{(takeTarget.buyer_receives_crypto ?? takeTarget.amount_crypto * 0.93).toFixed(6)} {takeTarget.crypto}</span></div>
              </div>
            )}
            {takeTarget.side === 'buy' && (
              <div className="bg-rose-50 border border-rose-200 rounded-2xl p-3.5 mb-4 text-sm" data-testid="take-seller-preview">
                <div className="flex justify-between text-rose-600 text-xs mb-1"><span>{p.commission7 || 'Commission 7%'}</span><span className="font-mono-data">−{(takeTarget.amount_crypto * 0.07).toFixed(6)} {takeTarget.crypto}</span></div>
                <div className="flex justify-between font-bold text-gray-900"><span>{p.buyerWillGet || "Transmis à l'acheteur"}</span><span className="font-mono-data">{(takeTarget.buyer_receives_crypto ?? takeTarget.amount_crypto * 0.93).toFixed(6)} {takeTarget.crypto}</span></div>
              </div>
            )}
            <label className="text-sm font-semibold text-gray-600 mb-2 block">
              {takeTarget.side === 'sell' ? (p.walletAddr || 'Adresse de votre wallet') : (p.bankCard || 'Votre carte bancaire')}
            </label>
            <div className="relative mb-5">
              <input type="text" value={takeInput} onChange={(e) => setTakeInput(e.target.value)}
                placeholder={takeTarget.side === 'sell' ? 'bc1q... / 0x... / TR...' : '4111 1111 1111 1111'}
                className="w-full bg-[#F5F6F7] rounded-2xl px-5 py-3.5 pr-24 outline-none font-mono-data" data-testid="take-order-input" />
              {takeTarget.side === 'buy' && detectCardBrand(takeInput) && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] font-extrabold text-white px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: detectCardBrand(takeInput).color }} data-testid="take-card-brand-badge">
                  {detectCardBrand(takeInput).brand}
                </span>
              )}
            </div>
            <div className="flex space-x-3">
              <button onClick={confirmTake} className="flex-1 btn-pill-dark py-3.5" data-testid="confirm-take-btn">{p.takeOrder || 'Confirmer'}</button>
              <button onClick={() => setTakeTarget(null)} className="flex-1 btn-pill-ghost py-3.5">{p.cancelBtn || 'Annuler'}</button>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default TradingDirect;
